import type ExtensibilityNotification from './ExtensibilityNotification';
import generateMessageId from './generateMessageId';
import updatePersistentNotifications from './updatePersistentNotifications';
import WebExtNotificationTypeType from 'owa-service/lib/contract/WebExtNotificationTypeType';
import { Adapter, getAdapter } from 'owa-addins-adapters';
import {
    addOrReplaceExtensibilityNotification,
    getAllMessageIds,
} from './ExtensibilityNotificationManager';
import { lazyGetItemProperties } from 'owa-get-item-manager';

/**
 * It will be used to fetch Notification data from getItem call
 * This skips the getAllClientExtensionsNotifications call
 * @param hostItemIndex the ext hostItemIndex for the given item
 * @param targetWindow to fetch the frameworkComponentId
 * @param itemId the itemId for the item (useful for compose)
 * @returns a promise of type void
 */

export default async function initializeExtensibilityNotificationsFromGetItem(
    hostItemIndex: string,
    targetWindow: Window,
    itemId: string
): Promise<void> {
    const adapter: Adapter = getAdapter(hostItemIndex);
    let response = null;
    const frameworkComponentId =
        targetWindow == window
            ? 'exfc_main'
            : targetWindow?.document.querySelector('[id^="exfc_"]')?.id;

    if (!adapter.addNotificationMessage) {
        return;
    }

    // adds notifications created from UIless add-ins of inactive host items
    const activeMessageIds = getAllMessageIds(hostItemIndex);
    activeMessageIds.map(activeMessageId =>
        adapter.addNotificationMessage(activeMessageId, frameworkComponentId)
    );

    try {
        const getItemProperties = await lazyGetItemProperties.import();
        response = await getItemProperties(itemId, 'WebExtNotifications');
    } catch (e) {
        /*
         *  There is an error when trying to get item properties from server.
         *  We silently discard the error and will retry once the item is visited again
         */
    }

    if (response) {
        const apps = response.Apps || [];
        apps.forEach(app => {
            let needsUpdateToServer: boolean = false;
            app.app.Notifications.forEach(notification => {
                const extNotification: ExtensibilityNotification = {
                    ExtensionId: app.app.Id,
                    Persistent: true,
                    Key: notification.Key,
                    Type: notification.Type,
                    Message: notification.Message,
                    Icon: notification.Icon,
                };

                if (notification.Type === WebExtNotificationTypeType.ErrorMessage) {
                    extNotification.Persistent = false;
                    needsUpdateToServer = true;
                }

                const messageId = generateMessageId(hostItemIndex, app.app.Id, notification.Key);
                addOrReplaceExtensibilityNotification(
                    hostItemIndex,
                    app.app.Id,
                    messageId,
                    extNotification
                );
                adapter.addNotificationMessage(messageId, frameworkComponentId);
            });

            if (needsUpdateToServer) {
                updatePersistentNotifications(hostItemIndex, app.app.Id);
            }
        });
    }
}
